import ReactDOM from 'react-dom'
import { MutableRefObject, useMemo, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { IMarkerWithStepName, IStep } from 'src/types'

import { PreviousPage, NextPage } from '../navigation'
import { MapView } from '../map-view'
import { ContainerSt, MapWrapper } from './journey-map.styled'
import { DEFAULT_LANGUAGE, PATHS, ROUTES } from 'src/constants'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { useTranslation } from 'react-i18next'

interface IProps {
  steps: IStep[]
  satelliteView: boolean
  scrollToRef?: MutableRefObject<HTMLSpanElement>
  markerColor?: string
  isImpacts?: boolean
  isTopDivider?: boolean
  accauntId?: string
  navButtonsTopOffset?: number
  mapHeight?: string
  mapMaxHeight?: string
  navVariant?: 'default' | JOURNEY_PAGE_TEMPLATE_ID
  prevLabel?: string
  nextLabel?: string
  navBackgroundColor?: string
  navTextColor?: string
}

export const JourneyMap = (props: IProps) => {
  const { simpleProductId } = useParams()
  const [t, i18n] = useTranslation()
  const location = useLocation()
  const portalContainerRef = useRef(null)

  const productLabel = t('journey-navigation-product-button')
  const journeyLabel = t('journey-navigation-journey-button')

  const markers = useMemo(() => {
    const groupedMarkers: Record<string, IMarkerWithStepName[]> = {}

    props.steps.forEach((step) => {
      step.locations.forEach((location) => {
        if (
          location.latitude !== 0 &&
          location.longitude !== 0 &&
          location.latitude !== null &&
          location.longitude !== null &&
          location.location_address &&
          location.location_address.trim() !== ''
        ) {
          const key = `${location.latitude / 10_000},${location.longitude / 10_000}`

          if (!groupedMarkers[key]) {
            groupedMarkers[key] = []
          }

          groupedMarkers[key].push({
            latitude: location.latitude / 10_000,
            longitude: location.longitude / 10_000,
            stepName: step.name,
            stepLocation: location.location_name,
            stepId: step.id,
          })
        }
      })
    })

    return Object.values(groupedMarkers)
  }, [props.steps])

  if (!portalContainerRef.current) {
    portalContainerRef.current = document.createElement('div')
    portalContainerRef.current.style.cssText = `
      position: absolute;
      top: 15px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 99999;
      max-width: 800px;
      width: 100%;
    `
    document.body.appendChild(portalContainerRef.current)
  }

  return (
    <>
      <ContainerSt
        id="index__components-journey-map__containerst__l78"
        mapHeight={props?.mapHeight}
        mapMaxHeight={props?.mapMaxHeight}
      >
        <MapWrapper
          id="index__components-journey-map__mapwrapper__l79"
          mapHeight={props?.mapHeight}
          mapMaxHeight={props?.mapMaxHeight}
        >
          <MapView
            markerColor={props.markerColor}
            satelliteView={props.satelliteView}
            markers={markers}
            steps={props.steps}
            scrollToRef={props.scrollToRef}
          />
        </MapWrapper>
      </ContainerSt>
      {portalContainerRef.current &&
        ReactDOM.createPortal(
          <>
            <PreviousPage
              isTopDivider={props?.isTopDivider}
              isImpactPage
              to={
                location.state?.from
                  ? location.state?.from
                  : location.pathname.replace(`/${PATHS.JOURNEY}`, '')
              }
              absolute
              topOffset={props?.navButtonsTopOffset}
              variant={props.navVariant ?? 'default'}
              label={props.prevLabel ?? location.state?.previousLabel ?? productLabel}
              backgroundColor={props.navBackgroundColor}
              textColor={props.navTextColor}
            />

            <NextPage
              isTopDivider={props?.isTopDivider}
              isImpactPage
              to={
                props.isImpacts
                  ? `${location.pathname.replace(ROUTES.JOURNEY, ROUTES.IMPACTS)}`
                  : i18n.language === DEFAULT_LANGUAGE
                    ? `/${ROUTES.ACCOUNT}/${props?.accauntId}`
                    : `/${i18n.language}/${ROUTES.ACCOUNT}/${props?.accauntId}`
              }
              state={{
                from: location.pathname,
                productId: simpleProductId,
                previousLabel: journeyLabel,
              }}
              topOffset={props?.navButtonsTopOffset}
              variant={props.navVariant ?? 'default'}
              label={props.nextLabel}
              backgroundColor={props.navBackgroundColor}
              textColor={props.navTextColor}
            />
          </>,
          portalContainerRef.current,
        )}
    </>
  )
}
